import React from 'react';
import prepDisplay from '../../../../common/utility/DisplayPrep';
import { FormField } from '../../components/FormField';
import { TextField } from '../../../TextField';
import { SelectField } from '../../../SelectField';
import { SwitchField } from '../../../SwitchField';
import { CheckboxField } from '../../../CheckboxField';
import { DatePicker, convertUTCToLocalDate, convertLocalToUTCDate } from '../../../DatePicker';
import { Object } from 'core-js';
import { format } from 'date-fns';

const optionsSortFns = {
    alphabetical: (a, b) => a.label.localeCompare(b.label)
};

const prepSelectOptions = (options, optionsSort) => {
    const transformedOptions = !Array.isArray(options)
        ? Object.entries(options).reduce((agg, [k, v], i) => {
              agg.push({
                  label: v,
                  value: k
              });
              return agg;
          }, [])
        : options;

    return optionsSort && Object.hasOwn(optionsSortFns, optionsSort)
        ? transformedOptions.sort(optionsSortFns[optionsSort])
        : transformedOptions;
};

const prepSelectValue = (value, options, selectClearable) => {
    const selectedOption = options.find((v, i) => {
        return v.value === value;
    });

    return selectClearable ? selectedOption ?? null : selectedOption;
};

const InputMapper = ({
    model,
    data,
    onChange,
    errors,
    headless,
    menuPortalDocument,
    compact,
    noMarginReadOnly,
    children,
    formFieldBasis
}) => {
    const map = {
        text: (props) => (headless ? props : <TextField {...props} />),
        select: (props) => (headless ? props : <SelectField {...props} menuPortalDocument={menuPortalDocument} />),
        multiline: (props) => (headless ? props : <TextField multiline {...props} />),
        checkbox: ({ size, ...props }) => {
            return headless ? (
                {
                    ...props,
                    ...{
                        checked: props.value,
                        onChange: (event) => {
                            props.onChange({
                                ...{
                                    target: {
                                        ...event.target,
                                        ...{ value: !props.value }
                                    }
                                }
                            });
                        }
                    }
                }
            ) : (
                <CheckboxField
                    {...{
                        ...props,
                        ...{
                            checked: typeof props.value !== 'undefined' ? Boolean(props.value) : false,
                            label: typeof props.checkboxlabel === 'string' ? props.checkboxlabel : props.label
                        },
                        onChange: (event) => {
                            props.onChange({
                                ...{
                                    target: {
                                        ...event.target,
                                        ...{ value: !props.value }
                                    }
                                }
                            });
                        }
                    }}
                />
            );
        },
        switch: (props, model) => {
            const changeHandler = (event) => {
                let value = event.target.value;
                if (typeof model.enum === 'object' && Object.keys(model.enum).length === 2) {
                    return value
                        ? props.onChange({
                              target: {
                                  ...event.target,
                                  ...{ value: Object.keys(model.enum)[0] }
                              }
                          })
                        : props.onChange({
                              target: {
                                  ...event.target,
                                  ...{ value: Object.keys(model.enum)[1] }
                              }
                          });
                }
                return props.onChange(v);
            };
            return headless ? (
                {
                    ...props,
                    ...{
                        onChange: changeHandler,
                        label: model.switchLabels,
                        checked:
                            typeof model.enum === 'object' && Object.keys(model.enum).length === 2
                                ? props.value === Object.keys(model.enum)[1]
                                : props.checked
                    }
                }
            ) : (
                <SwitchField
                    {...{
                        ...props,
                        ...{
                            onChange: changeHandler,
                            label: model.switchLabels
                        }
                    }}
                />
            );
        },
        date: (props) => {
            const changeHandler = (newLocalDate) => {
                return props.onChange({
                    value: convertLocalToUTCDate(newLocalDate)
                });
            };

            return headless ? (
                {
                    ...props,
                    ...{
                        onChange: changeHandler,
                        selected: props.value ? convertUTCToLocalDate(props.value) : undefined
                    }
                }
            ) : (
                <DatePicker
                    {...{
                        ...props,
                        ...{
                            onChange: changeHandler,
                            selected: props.value ? convertUTCToLocalDate(props.value) : undefined
                        }
                    }}
                />
            );
        },
        valueDisplay: (props, model) => {
            let displayValue = prepDisplay(props.value, model);
            return headless ? (
                {
                    ...props,
                    ...{
                        value: displayValue
                    }
                }
            ) : (
                <TextField
                    {...{
                        ...props,
                        ...{ value: displayValue }
                    }}
                />
            );
        },
        valueDisplayMultiline: (props, model) => {
            let displayValue = prepDisplay(props.value, model);
            return headless ? (
                {
                    ...props,
                    ...{
                        value: displayValue,
                        multiline: true
                    }
                }
            ) : (
                <TextField
                    {...{
                        ...props,
                        ...{
                            value: displayValue,
                            multiline: true
                        }
                    }}
                />
            );
        }
    };

    const onChangeType = (uiConf, e) => {
        let input = uiConf.input;
        if (typeof input === 'function') {
            input = input(data);
        }

        switch (input) {
            case 'select':
            case 'date':
                return onChange(uiConf.accessor, e.value);
            case 'checkbox':
                return onChange(uiConf.accessor, e.target.value);
            default:
                return onChange(uiConf.accessor, e.target.value);
        }
    };

    let inputMap = model.map((v, i) => {
        let value = '';
        if (data !== undefined) {
            value = data[v.accessor] === undefined ? '' : data[v.accessor];
            if (typeof v.accessorOverride === 'function') {
                value = v.accessorOverride(value || data);
            }
        }
        let input = null;
        if (typeof v.input === 'function') {
            input = v.input(data);
        } else {
            input = v.input;
        }

        if (input === null) {
            return null;
        }

        const isReadOnly = ['valueDisplay', 'valueDisplayMultiline'].includes(input);
        let inputProps = v?.inputProps ?? {};
        let props = {
            value,
            onChange: (e) => onChangeType(v, e),
            name: v.accessor,
            id: v.accessor,
            key: i,
            error: typeof errors[v.accessor] === 'string' ? errors[v.accessor] : '',
            label: typeof v.label === 'function' ? v.label(data) : v.label,
            readOnly: isReadOnly,
            ...inputProps
        };

        if (compact) {
            props.size = 'sm';
        }
        if (input === 'select') {
            if (typeof v.options !== 'object' && typeof v.enum === 'object') {
                v.options = v.enum;
            }
            if (typeof v.options === 'object') {
                props.options = prepSelectOptions(v.options, v?.optionsSort);
                props.value = prepSelectValue(value, props.options, v.selectClearable);
                props.isOptionDisabled = (option) => option.disabled === true;
            }
        }

        if (headless) {
            return {
                input,
                fieldProps: {
                    label: typeof v.label === 'string' ? v.label : '',
                    labelFor: v.accessor,
                    error: props.error,
                    readOnly: isReadOnly
                },
                inputProps: map[input](props, v)
            };
        } else {
            const label =
                typeof v.label === 'function'
                    ? v.label(data)
                    : typeof v.label === 'string' && !v?.inputProps?.checkboxlabel
                    ? v.label
                    : '';
            return (
                <FormField
                    label={label}
                    labelFor={v.accessor}
                    key={i}
                    error={props.error}
                    readOnly={isReadOnly}
                    noMargin={noMarginReadOnly && isReadOnly}
                    compact={compact}
                    formFieldBasis={formFieldBasis}
                >
                    {map[input](props, v)}
                </FormField>
            );
        }
    });
    if (headless) {
        return children(
            inputMap.reduce((agg, v, i) => {
                if (v !== null) {
                    agg[v.fieldProps.labelFor] = v;
                }
                return agg;
            }, {})
        );
    } else {
        return inputMap.map((v) => v);
    }
};

export default InputMapper;
